import React, { useEffect, useMemo }  from 'react';
import { TextLinkCaret } from '@vds/buttons';
import { Carousel } from '@vds/carousels';
import {  Flex, Block, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../../../components/common/StyledComponent/CommonStyledComponent';
import AIRecommendedCard from './AIRecommendedCard';
import styled from 'styled-components';
import {contentClick} from "../../../common/Analytics/GAWrapper.jsx";
import {findCourseById, getSuggestionData} from "../../../utility/utils.js"; 

const CarouselWrapper = styled.div`
 filter: drop-shadow(0px 16px 24px rgba(0, 0, 0, 0.049));

  @media (min-width: 1024px) {
    div[class^=SlotContainer-VDS] {
      flex: 0 0 337.5px;
    }

    span[class^=IconButton-VDS] {
      margin-right: 0px;
      margin-left: 0px;
    }
  }

  @media (min-width: 1024px) and (max-width: 1232px) {
    span[class^=IconButton-VDS] {
      margin-right: 12px;
      margin-left: 12px;
    }
  }

  @media (max-width: 767px) {
    span[class^=IconButton-VDS] {
      margin-right: 8px;
      margin-left: 8px;
    }
  }

  @media (min-width: 767px) and (max-width: 1023px) {
    div[class^=SlotContainer-VDS] {
      flex: 0 0 344px;
    }
  }
`;

const AIRecommendationCarouselSection = ({ courseData = [],askAiData , onViewAllMixed}) => {
  console.log('courseData:', courseData);
  const TOTAL_AVAILABLE_TYPES = 5;
  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  const isTablet =
    window.innerWidth > MOBILE_BREAKPOINT &&
    window.innerWidth <= TABLET_BREAKPOINT;

    const newSuggestionsData = getSuggestionData();
    const ai_searchJsonData = JSON.parse(localStorage.getItem('aisearch_landingpage_data')) || {};
    const ai_suggestionData = newSuggestionsData || JSON.parse(localStorage.getItem('aisearch_suggestions_data'))?.data || {};


  // 1. Filter the courseData to courses and quick tips and
  //    normalize each item so downstream components (AIRecommendedCard)
  //    can rely on a `launchUrl` field. Source items only expose `link`.
const cappedCourseData = useMemo(() => {
    if (!courseData || courseData.length === 0) return [];

    // Define seenIds here so the reducer can use it
    const seenIds = new Set(); 

    return courseData.reduce(
      (acc, item) => {
        const itemType = (item.type || '').toLowerCase();
        
        // 1. Filter out unwanted types (including 'page')
        if (!itemType || itemType === 'instructor' || itemType === 'spotlight' || itemType === 'gated' || itemType === 'nongated') {
          return acc;
        }

         let processedItem = { ...item };
        // Extract ID from link for page types
        if (itemType === 'page' && typeof processedItem?.link === 'string' && /(?:course-details)\//i.test(processedItem?.link)) {
          const matchPageTypeID = processedItem.link.match(/(?:course-details)\/([^/?#]+)/i);
          if (matchPageTypeID && matchPageTypeID[1]) {
            processedItem.id = decodeURIComponent(matchPageTypeID[1]);
            console.log('Extracted ID from link for page type:', processedItem.id);
          }
           if (processedItem.id) {
            const courseDetailsObj = findCourseById(processedItem.id, ai_suggestionData, processedItem, ai_searchJsonData);
            if (courseDetailsObj && courseDetailsObj.resource) {
              console.log('Found course details for ID:', processedItem.id, courseDetailsObj.resource);
              // Merge the fetched resource details into the item
              processedItem = { ...processedItem, ...courseDetailsObj.resource };
              console.log('Merged course details into item:', processedItem);
            } else {
              // --> DISCARD ITEM if course details/resource are missing
              console.log('Discarding item due to missing course details:', processedItem.id);
              return acc; 
            }
          } else {
             // --> DISCARD ITEM if ID couldn't be extracted from the page link
             return acc;
          }
        }
        // 2. Enforce the link / format rules from your first filter
        const hasValidLink = processedItem.link && processedItem.link.includes('/course-details/course:');
        const isQuickTip = processedItem.format?.toLowerCase() === 'quick tip';
        
        if (!hasValidLink && !isQuickTip) {
            return acc;
        }

        // 3. Identify duplicates using id (or eventSerpName as fallback)
        // Added item.link as a tertiary fallback to prevent undefined issues
        const uniqueId = processedItem.id || processedItem.eventSerpName || processedItem.link; 
        if (!uniqueId || seenIds.has(uniqueId)) {
          return acc;
        }

        const category = (processedItem.category || '').toLowerCase();
        let mappedType = '';

        // 4. Normalize categories to match your heading logic
        if (category.includes('event')) {
          mappedType = 'Events';
        } else if (category.includes('course')) {
          mappedType = 'Courses';
        } else if (category.includes('tip')) {
          mappedType = 'Quick tips';
        } else {
          return acc; 
        }

        // Initialize count if it doesn't exist
        if (!acc.counts[mappedType]) {
          acc.counts[mappedType] = 0;
        }

        // 5. Only add the item if we haven't reached the limit of 8 for this category
        if (acc.counts[mappedType] < 8) {
          seenIds.add(uniqueId);
          acc.counts[mappedType] += 1;
          
          // Push the mapped item with the launchUrl property included
          acc.filteredData.push({
             ...processedItem,
             launchUrl: processedItem.launchUrl || processedItem.link,
          });
        }
        
        return acc;
      },
      { counts: {}, filteredData: [] }
    ).filteredData; // Return the final filtered array
  }, [courseData, ai_suggestionData, ai_searchJsonData]);

  // 2. Extract unique types for Headings, CTA text, and Search URL Filters
  const uniqueTypes = useMemo(() => {
    const types = new Set();
    cappedCourseData.forEach((item) => {
      const category = (item.category || '').toLowerCase();
      if (category.includes('event')) types.add('Events');
      else if (category.includes('course')) types.add('Courses');
      else if (category.includes('tip')) types.add('Quick tips');
      else if (category.includes('person')) types.add('In-Person Events'); 
      else types.add('Resources');
    });
    return Array.from(types);
  }, [cappedCourseData]);

  // 3. Map askAiData to Headings and CTA Text
  const displayTexts = useMemo(() => {
    const texts = askAiData?.resultsPageTexts?.recommendSection || {};
    const cleanHTML = (str) => (str ? str.replace(/<[^>]*>?/gm, '').trim() : '');

    // Fallbacks for multiple types or mixed categories
    let heading = cleanHTML(texts.topRecommLbl) || 'Recommended for you';
    let ctaText = texts.viewAllRecommTxt || 'View all';
    let subheading = 'If you’d like to go deeper, here are a few additional resources to help you get started.';

    // Specific text overrides if there is exactly one unified type
    if (uniqueTypes.length === 1) {
      const type = uniqueTypes[0];

      if (type === 'Courses') {
        heading = cleanHTML(texts.recommendCrseLabel) || `Recommended ${type}`;
        ctaText = texts.viewAllCrseTxt || `View all ${type.toLowerCase()}`;
      } else if (type === 'Events') {
        heading = cleanHTML(texts.recommendEventLabel) || `Recommended ${type}`;
        ctaText = texts.viewAllEventTxt || `View all ${type.toLowerCase()}`;
      } else if (type === 'In-Person Events') {
        heading = cleanHTML(texts.recommendIpEventLabel) || `Recommended ${type}`;
        ctaText = texts.viewAllEventTxt || `View all ${type.toLowerCase()}`;
      }
       else if (type === 'Quick tips') {
        heading = cleanHTML(texts.recommendQtLabel) || `Recommended ${type}`;
        ctaText = texts.viewAllQtTxt || `View all ${type.toLowerCase()}`;
      } else {
        // Fallback for paths or other types not strictly mapped in askAiData
        heading = `Recommended ${type}`;
        ctaText = `View all ${type.toLowerCase()}`;
      }
      
      subheading = `If you’d like to go deeper, here are a few additional ${type.toLowerCase()} to help you get started.`;
    }

    return { heading, ctaText, subheading };
  }, [uniqueTypes, askAiData]);

  const showCTA = courseData.length > 3;
  const ctaText = uniqueTypes.length === 1 ? `View all ${uniqueTypes[0].toLowerCase()}` : 'View all';
   const ctaLabel =
    (displayTexts?.ctaText && displayTexts.ctaText.trim()) ||
    'View all recommendations';
  
  const handleCTAClick = () => {
    let targetFilter = 'all';
      if (uniqueTypes.length === 1) {
          const type = uniqueTypes[0];
          if (type === 'Courses') targetFilter = 'courses';
          else if (type === 'Quick tips') targetFilter = 'quicktips';
          else if (type === 'Events') targetFilter = 'virtualevents';
          else if (type === 'In-Person Events') targetFilter = 'inpersonevents';
      }
      console.log('CTA clicked. Target filter:', targetFilter);
      contentClick(ctaLabel, window.location.href, '', targetFilter != 'all' ? `view_all_${targetFilter}` : 'view_all_recommendations', );
      if (onViewAllMixed) {
        onViewAllMixed(targetFilter);
      }   
  };

  if (!cappedCourseData || cappedCourseData.length === 0) {
    return null;
  }
 

  return (
    <Block marginTop="32px">
      <Flex
        flexDirection={isMobile ? 'column' : 'row'}
        padding={isMobile ? '0 16px' : '0 24px'}
        alignItems='flex-start'
        gap={isMobile ? '16px' : '24px'}
        justifyContent='space-between'
      >
        <Flex flexDirection='column' gap='8px'>
          <div className='vds-title-sm mobile-vds-title-xs font-eDS-bold'>
            {displayTexts.heading || 'Recommended for you'}
          </div>
          {/* <div className='vds-body-lg'>
            {displayTexts.subheading || 'If you’d like to go deeper, here are a couple of additional courses related to growth marketing:'}
          </div> */}
        </Flex>
        <Flex width='fit-content' flexWrap='nowrap' style={{ whiteSpace: 'nowrap' }}>
          <TextLinkCaret
            aria-label={ctaLabel}
            surface='light'
            disabled={false}
            iconPosition='right'
            onClick={handleCTAClick}
          >
              {ctaLabel}
          </TextLinkCaret>
        </Flex>
      </Flex>
      <CarouselWrapper className="mt-6X">
        <Carousel
          layout={isMobile ? '1UP' : isTablet ? '2UP' : '3UP'}
          gutter={isMobile ? '12px' : '24px'}
          peek={isMobile || isTablet ? 'standard' : 'none'}
          surface="light"
          paginationInset="12px"
          pagination={{
            kind: 'lowContrast',
            hideBorder: true,
            floating: true,
          }}
          paginationDisplay="persistent"
          aspectRatio="none"
          data={cappedCourseData}
          renderItem={(itemData) => (
            <AIRecommendedCard item={itemData} isMobile={isMobile} askAiData={askAiData} width='100%' />
          )}
        />
      </CarouselWrapper>
    </Block>
  );
};

export default AIRecommendationCarouselSection;
